import { PublisherActions, SubscriberProperties, VisioService } from '@agent/core/channel-digit/visio/visio.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ProfileService, UntilDestroy, untilDestroyed } from '@contact/upgrade';

@UntilDestroy()
@Component({
  selector: 'cng-visio-publisher',
  templateUrl: 'visio-publisher.component.html',
  styleUrls: ['./visio-publisher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisioPublisherComponent implements OnInit {
  @ViewChild('visioPublisherContainer', { static: true }) visioPublisherContainer: ElementRef;
  @ViewChild('screenSharingPublisherContainer', { static: true }) screenSharingPublisherContainer: ElementRef;

  localVideoEnabled: boolean = true;
  localAudioEnabled: boolean = true;
  session: OT.Session | undefined = undefined;

  hasScreenSharing$ = this.visioService.publisherScreenStream$.pipe(map(stream => !!stream));

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly visioService: VisioService,
    private readonly profileService: ProfileService
  ) {
    this.session = this.visioService.session;

    // ADD A ENUM FOR TYPES AND STATES
    this.visioService.publisherActions$.pipe(untilDestroyed(this)).subscribe(action => {
      if (!this.visioService.publisherCamera) {
        return;
      }
      switch (action) {
        case PublisherActions.ON_SOUND:
          this.localAudioEnabled = true;
          this.visioService.publisherOptionsStates('audio', this.localAudioEnabled);
          break;
        case PublisherActions.OFF_SOUND:
          this.localAudioEnabled = false;
          this.visioService.publisherOptionsStates('audio', this.localAudioEnabled);
          break;
        case PublisherActions.TOGGLE_SOUND:
          this.localAudioEnabled = !this.localAudioEnabled;
          this.visioService.publisherOptionsStates('audio', this.localAudioEnabled);
          break;
        case PublisherActions.ON_VIDEO_CAMERA:
          this.localVideoEnabled = true;
          this.visioService.publisherOptionsStates('video', this.localVideoEnabled);
          break;
        case PublisherActions.OFF_VIDEO_CAMERA:
          this.localVideoEnabled = false;
          this.visioService.publisherOptionsStates('video', this.localVideoEnabled);
          break;
        case PublisherActions.TOGGLE_VIDEO_CAMERA:
          this.localVideoEnabled = !this.localVideoEnabled;
          this.visioService.publisherOptionsStates('video', this.localVideoEnabled);
          break;
        case PublisherActions.ON_VIDEO_SCREEN:
          this.startScreenSharing();
          break;
        case PublisherActions.OFF_VIDEO_SCREEN:
          this.startScreenSharing();
          break;
        case PublisherActions.TOGGLE_VIDEO_SCREEN:
          this.startScreenSharing();
          break;
        default:
          break;
      }
      this.cdr.detectChanges();
    });
    this.visioService.subscriberChange$.pipe(untilDestroyed(this)).subscribe(event => {
      if (
        this.visioService.session &&
        this.visioService.session.connection &&
        event.stream.connection.connectionId === this.visioService.session.connection.connectionId
      ) {
        switch (event.changedProperty) {
          case SubscriberProperties.hasAudio:
            this.localAudioEnabled = event.newValue;
            break;
          case SubscriberProperties.hasVideo:
            this.localVideoEnabled = event.newValue;
            break;
          default:
            break;
        }
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit() {
    if (!!this.session) {
      if (this.session['isConnected']()) {
        this.startCameraSharing();
      }
      this.session.on('sessionConnected', () => this.startCameraSharing());
    }
  }

  hasPermission$(permission): Observable<boolean> {
    return this.profileService.hasPermission$(permission);
  }

  getState(active: boolean): string {
    return active ? 'ENABLED' : 'DISABLED';
  }

  startCameraSharing() {
    if (!this.visioPublisherContainer) {
      return;
    }
    this.visioService.publishCamera(this.visioPublisherContainer);
  }

  startScreenSharing() {
    if (!this.screenSharingPublisherContainer) {
      return;
    }
    this.visioService.publishScreen(this.screenSharingPublisherContainer);
  }

  toggleLocalAudio() {
    this.localAudioEnabled = !this.localAudioEnabled;
    this.visioService.publisherOptionsStates('audio', this.localAudioEnabled);
  }

  toggleLocalVideo() {
    this.localVideoEnabled = !this.localVideoEnabled;
    this.visioService.publisherOptionsStates('video', this.localVideoEnabled);
  }
}
